import { useCallback, useEffect, useState } from 'react';
import { Box, Button, Fade, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { Cookie } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  needsConsentBanner,
  readConsent,
  resetConsent,
  setConsent,
  subscribeToConsent,
} from '../../utils/consent/consentStore';
import { applyColors } from '../../features/application/styles/theme';

const TERMS_URL = 'https://raisemyfunds.co/wp-content/uploads/2024/08/TCs-RaiseMyFunds.pdf';

const panelSx = {
  position: 'fixed',
  left: { xs: 12, md: 20 },
  right: { xs: 12, md: 'auto' },
  bottom: { xs: 12, md: 20 },
  // Above MUI modals (1300) and snackbars (1400): the banner has to stay clickable
  // whatever is on screen, including a full-page overlay.
  zIndex: 1500,
  width: { xs: 'auto', md: 400 },
  maxWidth: '100%',
  p: 2.5,
  borderRadius: '16px',
  border: `1px solid ${applyColors.border}`,
  backgroundColor: applyColors.panel,
  boxShadow: '0 16px 48px rgba(0,0,0,0.45)',
};

const reopenSx = {
  position: 'fixed',
  left: { xs: 12, md: 20 },
  bottom: { xs: 12, md: 20 },
  zIndex: 1499,
  width: 34,
  height: 34,
  color: applyColors.textMuted,
  backgroundColor: applyColors.panel,
  border: `1px solid ${applyColors.border}`,
  opacity: 0.45,
  transition: 'opacity 120ms ease',
  '&:hover': { opacity: 1, backgroundColor: applyColors.panelAlt },
};

/**
 * Consent gate for the marketing pixels. Shown only where prior consent is required;
 * everywhere else the consent is granted silently and nothing is rendered.
 */
const CookieBanner = () => {
  const { t } = useTranslation('cookies');
  const [open, setOpen] = useState(false);
  const [decided, setDecided] = useState(false);

  // No pixel call here: the inline bootstrap of index.html already applied the stored
  // decision. This only decides whether the visitor still has to be asked.
  useEffect(() => {
    setOpen(needsConsentBanner());
    setDecided(Boolean(readConsent()));
    return subscribeToConsent(() => setDecided(Boolean(readConsent())));
  }, []);

  const choose = useCallback((granted: boolean) => {
    setConsent(granted ? 'granted' : 'denied');
    setOpen(false);
  }, []);

  const reopen = useCallback(() => {
    resetConsent();
    setOpen(true);
  }, []);

  if (!open) {
    if (!decided) return null;
    return (
      <Tooltip title={t('MANAGE')} placement="right">
        <IconButton sx={reopenSx} onClick={reopen} aria-label={t('MANAGE')}>
          <Cookie size={16} />
        </IconButton>
      </Tooltip>
    );
  }

  return (
    <Fade in appear>
      <Box sx={panelSx} role="dialog" aria-label={t('TITLE')}>
        <Stack spacing={1.5}>
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#FFFFFF' }}>
            {t('TITLE')}
          </Typography>
          <Typography sx={{ fontSize: 13, lineHeight: 1.55, color: applyColors.textMuted }}>
            {t('BODY')}{' '}
            <Box
              component="a"
              href={TERMS_URL}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ color: applyColors.accentSoft, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
            >
              {t('TERMS')}
            </Box>
          </Typography>
          <Stack direction="row" spacing={1} justifyContent="flex-end" pt={0.5}>
            <Button
              onClick={() => choose(false)}
              sx={{
                textTransform: 'none',
                fontSize: 13,
                fontWeight: 600,
                color: applyColors.textMuted,
                borderRadius: '10px',
                px: 2,
                '&:hover': { backgroundColor: applyColors.panelAlt, color: '#FFFFFF' },
              }}
            >
              {t('REFUSE')}
            </Button>
            <Button
              onClick={() => choose(true)}
              sx={{
                textTransform: 'none',
                fontSize: 13,
                fontWeight: 700,
                color: '#FFFFFF',
                backgroundColor: applyColors.primary,
                borderRadius: '10px',
                px: 2.5,
                boxShadow: 'none',
                '&:hover': { backgroundColor: applyColors.primaryHover, boxShadow: 'none' },
              }}
            >
              {t('ACCEPT')}
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Fade>
  );
};

export default CookieBanner;
